/**
 * Regtank Constants and Configurations
 * Centralizes all constants, button styles, and configuration values
 */

/**
 * Button style configurations
 */
export const BUTTON_STYLES = {
  approve: {
    backgroundColor: "rgba(51, 143, 134, 1)",
    color: "rgba(255, 255, 255, 1)",
  },
  reject: {
    backgroundColor: "rgba(164, 47, 36, 1)",
    color: "white",
  },
  cancel: {
    backgroundColor: "rgb(250, 250, 250)",
    border: "true",
    color: "rgba(0, 0, 0, 1)",
    borderColor: "rgba(0, 0, 0, 1)",
  },
  confirm: {
    backgroundColor: "rgba(0, 0, 0, 1)",
  },
} as const;

/**
 * Modal title configurations
 */
export const MODAL_TITLES = {
  documentFront: "ID Document (Front)",
  documentBack: "ID Document (Back)",
  livenessVideo: "Liveness Check Video",
  livenessPhoto: "Liveness Check Photo",
  confirmApproval: "Confirm Approval",
  confirmRejection: "Confirm Rejection",
} as const;

/**
 * Table configuration
 */
export const TABLE_CONFIG = {
  maxHeight: "500px",
  density: "xs",
  paginationMode: "pages",
} as const;

/**
 * Tooltip labels
 */
export const TOOLTIP_LABELS = {
  attemptUsed: "Each verification attempt consists of 3 liveness checks",
} as const;

/**
 * Textarea configuration
 */
export const TEXTAREA_CONFIG = {
  minRows: 2,
  maxRows: 4,
  autosize: true,
} as const;

/**
 * Modal size configurations
 */
export const MODAL_SIZES = {
  auto: "auto",
  medium: "md",
  extraLarge: "xl",
} as const;

/**
 * Badge color mappings for verification results
 */
export const VERIFICATION_RESULT_COLORS = {
  PASSED: "green",
  FAILED: "red",
  EXPIRED: "gray",
} as const;

/**
 * Badge color mappings for reattempt approval
 */
export const REATTEMPT_APPROVAL_COLORS = {
  APPROVED: "green",
  REJECTED: "red",
} as const;

/**
 * Document detail type mappings
 */
export const DOCUMENT_DETAIL_TYPES = {
  documentFront: "documentFrontPhotoUrl",
  documentBack: "documentBackPhotoUrl",
  livenessSelfie: "livenessSelfieUrl",
  livenessVideo: "livenessVideoUrl",
} as const;

/**
 * Media view types
 */
export const MEDIA_VIEW_TYPES = {
  photo: "PHOTO",
  video: "VIDEO",
} as const;

/**
 * Instruction text
 */
export const INSTRUCTION_TEXT = {
  reattemptApproval:
    "Please provide a detailed remark before approving or rejecting Regtank reattempt.",
} as const;

/**
 * Confirmation messages
 */
export const CONFIRMATION_MESSAGES = {
  approve: (nameRole: string) =>
    `Are you sure you want to approve this verification reattempt for ${nameRole}?`,
  reject: (nameRole: string) =>
    `Are you sure you want to reject this verification reattempt for ${nameRole}?`,
} as const;

/**
 * Button labels
 */
export const BUTTON_LABELS = {
  approve: "APPROVE",
  reject: "REJECT",
  cancel: "CANCEL",
  confirm: "CONFIRM",
  viewFrontPhoto: "VIEW FRONT PHOTO",
  viewBackPhoto: "VIEW BACK PHOTO",
  viewPhoto: "VIEW PHOTO",
  viewVideo: "VIEW VIDEO",
} as const;

/**
 * Section headings
 */
export const SECTION_HEADINGS = {
  onboardingVerification: "Onboarding Identity Verification",
  independentLiveness: "Independent Liveness Check",
  verificationHistory: "Verification History",
  reattemptApproval: "Verification Reattempt Approval",
} as const;

/**
 * User Data Card table header configurations
 * Maps card types to their specific table structure
 */
export const USER_DATA_CARD_CONFIG = {
  onboarding: {
    statusKey: "onboardingStatus" as const,
    showAttemptColumn: true,
  },
  liveness: {
    statusKey: "verificationStatus" as const,
    showAttemptColumn: false,
  },
} as const;

/**
 * User Data Card table headers
 */
export const USER_DATA_CARD_HEADERS = {
  status: "Latest Status",
  lastAttempt: "Last Attempt On",
  attempts: "Attempt(s) Used",
} as const;

/**
 * Main verification tables section titles
 */
export const VERIFICATION_SECTION_TITLES = {
  onboardingVerification: "Onboarding Identity Verification",
  independentLiveness: "Independent Liveness Check",
} as const;
